import React, { useState, useEffect } from 'react'
import { Typography, Card, Switch, List, Tag, Space, Row, Col, Button, message, Spin, Modal, Form, Input, Select } from 'antd'
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, ReloadOutlined, SettingOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import axios from 'axios'

const { Title, Text } = Typography
const { Option } = Select

const System = () => {
  const [healthData, setHealthData] = useState(null)
  const [configItems, setConfigItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [configModalVisible, setConfigModalVisible] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    fetchSystemHealth()
    fetchSystemConfig()
  }, [])

  const fetchSystemHealth = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/system/health', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setHealthData(response.data)
    } catch (error) {
      console.error('Failed to fetch system health:', error)
      message.error('Failed to load system health')
    }
  }

  const fetchSystemConfig = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/system/config', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setConfigItems(response.data)
    } catch (error) {
      console.error('Failed to fetch system config:', error)
      message.error('Failed to load system configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleMaintenanceMode = async (enabled) => {
    Modal.confirm({
      title: `${enabled ? 'Enable' : 'Disable'} Maintenance Mode?`,
      icon: <ExclamationCircleOutlined />,
      content: enabled
        ? 'This will prevent all non-admin users from accessing the platform.'
        : 'This will allow users to access the platform normally.',
      onOk: async () => {
        try {
          const token = localStorage.getItem('token')
          await axios.post(`/system/maintenance-mode?enable=${enabled}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
          message.success(`Maintenance mode ${enabled ? 'enabled' : 'disabled'}`)
          fetchSystemConfig()
        } catch (error) {
          console.error('Failed to toggle maintenance mode:', error)
          message.error('Failed to update maintenance mode')
        }
      }
    })
  }

  const handleUpdateConfig = async (values) => {
    try {
      const token = localStorage.getItem('token')
      await axios.put(`/system/config/${selectedConfig.config_key}`, {
        config_value: values.config_value
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      message.success('Configuration updated successfully')
      setConfigModalVisible(false)
      setSelectedConfig(null)
      form.resetFields()
      fetchSystemConfig()
    } catch (error) {
      console.error('Failed to update config:', error)
      message.error('Failed to update configuration')
    }
  }

  const renderHealthIcon = (status) => {
    if (status === 'healthy' || status === 'current') {
      return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
    }
    if (status === 'unhealthy' || status === 'no data') {
      return <CloseCircleOutlined style={{ color: '#f5222d', fontSize: '20px' }} />
    }
    return <WarningOutlined style={{ color: '#faad14', fontSize: '20px' }} />
  }

  const getStatusTag = (status) => {
    if (status === 'healthy' || status === 'current') {
      return <Tag color="green">{status.toUpperCase()}</Tag>
    }
    if (status === 'unhealthy' || status === 'no data') {
      return <Tag color="red">{status.toUpperCase()}</Tag>
    }
    return <Tag color="orange">{status.toUpperCase()}</Tag>
  }

  const systemStatus = healthData?.components ? [
    { name: 'Database', status: healthData.components.database, details: 'PostgreSQL' },
    { name: 'API Server', status: healthData.components.api, details: 'FastAPI' },
    { name: 'Market Data', status: healthData.components.market_data, details: healthData.components.market_data }
  ] : []

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>
          <SettingOutlined className="mr-2" />
          System Configuration
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            fetchSystemHealth()
            fetchSystemConfig()
          }}
        >
          Refresh
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card title="System Health" loading={!healthData}>
            {healthData ? (
              <>
                <div className="mb-4">
                  <Text strong>Overall Status: </Text>
                  {getStatusTag(healthData.status)}
                </div>
                <List
                  dataSource={systemStatus}
                  renderItem={(item) => (
                    <List.Item>
                      <Space>
                        {renderHealthIcon(item.status)}
                        <Text strong>{item.name}</Text>
                        {getStatusTag(item.status)}
                        <Text type="secondary">{item.details}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
                <div className="mt-4">
                  <Text type="secondary">
                    Last checked: {healthData.timestamp ? new Date(healthData.timestamp).toLocaleString() : 'N/A'}
                  </Text>
                </div>
              </>
            ) : (
              <Spin />
            )}
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Configuration" loading={loading}>
            <List
              dataSource={configItems}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    item.config_type === 'boolean' && item.config_key === 'maintenance_mode' ? (
                      <Switch
                        checked={item.config_value}
                        onChange={handleToggleMaintenanceMode}
                      />
                    ) : item.config_type === 'boolean' ? (
                      <Switch
                        checked={item.config_value}
                        disabled={!item.is_editable}
                      />
                    ) : item.is_editable ? (
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedConfig(item)
                          form.setFieldsValue({ config_value: item.config_value })
                          setConfigModalVisible(true)
                        }}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Text>{item.config_value}</Text>
                    )
                  ]}
                >
                  <List.Item.Meta
                    title={item.config_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    description={item.description}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={`Edit Configuration: ${selectedConfig?.config_key}`}
        open={configModalVisible}
        onCancel={() => {
          setConfigModalVisible(false)
          setSelectedConfig(null)
          form.resetFields()
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateConfig}
        >
          <Form.Item
            name="config_value"
            label="Value"
            rules={[{ required: true, message: 'Please enter a value' }]}
          >
            {selectedConfig?.config_type === 'number' ? (
              <Input type="number" />
            ) : selectedConfig?.config_type === 'boolean' ? (
              <Select>
                <Option value={true}>True</Option>
                <Option value={false}>False</Option>
              </Select>
            ) : (
              <Input />
            )}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update
              </Button>
              <Button onClick={() => {
                setConfigModalVisible(false)
                setSelectedConfig(null)
                form.resetFields()
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default System
